<template>
  <v-container class="instandsetzung-container">
    <!-- Formular für neue Instandsetzung mit Glasmorphismus-Effekt -->
    <v-card class="status-overview-card mb-6">
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2">mdi-wrench</v-icon>
        Neue Instandsetzung
        <v-spacer></v-spacer>
        <v-btn
          icon
          class="mx-1"
          variant="text"
          @click="sparePartsDialog = true"
          color="success"
          v-tooltip="'Ersatzteile öffnen'"
        >
          <v-icon>mdi-barcode</v-icon>
        </v-btn>
        <v-btn
          icon
          class="mx-1"
          variant="text"
          @click="irisMatrixDialog = true"
          color="success"
          v-tooltip="'IRIS Code Matrix öffnen'"
        >
          <v-icon>mdi-table-search</v-icon>
        </v-btn>
        <v-chip color="success" size="small">Gerät registrieren</v-chip>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <v-card-text class="py-4">
        <v-row>
          <v-col cols="12" sm="6">
            <v-text-field
              ref="auftragsnummerField"
              v-model="auftragsnummer"
              label="Auftragsnummer (7-10 Zeichen)"
              variant="outlined"
              maxlength="10"
              @keyup.enter="openTypeDialog"
              @keypress="validateNumericInput"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              class="form-field"
              color="success"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-identifier</v-icon>
              </template>
              <template v-slot:append-inner>
                <v-btn
                  v-if="isMobileDevice"
                  icon
                  size="small"
                  variant="text"
                  color="success"
                  @click="openBarcodeScanner"
                  v-tooltip="'Barcode scannen'"
                  class="scanner-btn"
                >
                  <v-icon size="20">mdi-qrcode-scan</v-icon>
                </v-btn>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="menge"
              type="number"
              label="Menge"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              class="form-field"
              color="success"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-counter</v-icon>
              </template>
            </v-text-field>
          </v-col>

          <v-col cols="12">
            <v-textarea
              v-model="kommentar"
              label="Kommentar"
              variant="outlined"
              bg-color="rgba(30, 30, 40, 0.5)"
              hide-details="auto"
              auto-grow
              rows="3"
              class="form-field"
              color="success"
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small"
                  >mdi-comment-text-outline</v-icon
                >
              </template>
            </v-textarea>
          </v-col>
        </v-row>

        <!-- Verbesserte Action-Schaltfläche mit deutlicherem inaktiven Zustand -->
        <v-btn
          block
          :color="auftragsnummer.length >= 7 && auftragsnummer.length <= 10 ? 'success' : 'grey-darken-3'"
          class="mt-6 submit-btn"
          :class="{ 'disabled-btn': auftragsnummer.length < 7 || auftragsnummer.length > 10 }"
          size="large"
          elevation="2"
          @click="openTypeDialog"
          :disabled="auftragsnummer.length < 7 || auftragsnummer.length > 10"
        >
          <template v-if="auftragsnummer.length >= 7 && auftragsnummer.length <= 10">
            <v-icon start>mdi-keyboard</v-icon>
            Fortfahren mit Geräteauswahl
          </template>
          <template v-else>
            <span class="keyboard-icon-wrapper">
              <v-icon start>mdi-keyboard</v-icon>
              <span class="strike-through"></span>
            </span>
            Auftragsnummer benötigt (7-10 Zeichen)
          </template>
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- Darstellung gespeicherter Instandsetzungen -->
    <v-card
      class="status-overview-card"
      :class="{ 'maximized-card': isListMaximized }"
    >
      <v-card-title class="d-flex align-center">
        <v-icon color="primary" size="24" class="mr-2"
          >mdi-format-list-checks</v-icon
        >
        Gespeicherte Instandsetzungen
        <v-spacer></v-spacer>

        <div class="d-flex align-center">
          <v-tooltip location="bottom" text="Filter & Export">
            <template v-slot:activator="{ props }">
              <v-btn
                icon
                class="mx-1"
                variant="text"
                v-bind="props"
                @click="showFilters = !showFilters"
                color="success"
              >
                <v-icon>{{ showFilters ? 'mdi-filter-off' : 'mdi-filter-variant' }}</v-icon>
              </v-btn>
            </template>
          </v-tooltip>

          <v-tooltip location="bottom" text="Suchen">
            <template v-slot:activator="{ props }">
              <v-btn
                icon
                class="mx-1"
                variant="text"
                v-bind="props"
                @click="instSearchDialog = true"
                color="success"
              >
                <v-icon>mdi-magnify</v-icon>
              </v-btn>
            </template>
          </v-tooltip>
        </div>
      </v-card-title>

      <v-divider class="card-divider"></v-divider>

      <!-- Filter & Export Sektion -->
      <v-expand-transition>
        <div v-show="showFilters">
      <v-card-text class="py-3">
        <v-row>
          <v-col cols="12">
            <v-card class="filter-card elevation-0">
              <v-card-title class="text-subtitle-2 py-2">
                <v-icon size="small" class="mr-2">mdi-filter-variant</v-icon>
                Filter & Export
              </v-card-title>
              <v-card-text class="pb-2">
                <v-row dense align="center">
                  <!-- Monatsfilter -->
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="filterMonth"
                      :items="monthOptions"
                      label="Monat filtern"
                      clearable
                      density="compact"
                      variant="outlined"
                      prepend-inner-icon="mdi-calendar-month"
                      hide-details
                    ></v-select>
                  </v-col>

                  <!-- Gerätetyp-Filter -->
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="filterGeraetetyp"
                      :items="geraetetypFilterOptions"
                      label="Gerätetyp (optional)"
                      clearable
                      density="compact"
                      variant="outlined"
                      prepend-inner-icon="mdi-devices"
                      hide-details
                    ></v-select>
                  </v-col>

                  <!-- Reparaturtyp-Filter -->
                  <v-col cols="12" sm="6" md="3">
                    <v-select
                      v-model="filterReparaturtyp"
                      :items="reparaturtypFilterOptions"
                      label="Reparaturtyp (optional)"
                      clearable
                      density="compact"
                      variant="outlined"
                      prepend-inner-icon="mdi-tools"
                      hide-details
                    ></v-select>
                  </v-col>

                  <!-- Export-Buttons -->
                  <v-col cols="12" sm="6" md="3" class="d-flex align-center justify-center justify-sm-start gap-2">
                    <v-btn
                      @click="printFilteredData"
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-printer"
                      :disabled="filteredDataForExport.length === 0"
                      size="small"
                    >
                      Drucken
                    </v-btn>
                    <v-btn
                      @click="downloadCSV"
                      color="success"
                      variant="tonal"
                      prepend-icon="mdi-download"
                      :disabled="filteredDataForExport.length === 0"
                      size="small"
                    >
                      CSV
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Anzeige der Filterresultate -->
                <v-row v-if="filterMonth" dense class="mt-1">
                  <v-col cols="12">
                    <v-chip
                      color="success"
                      variant="tonal"
                      size="small"
                    >
                      <v-icon start size="small">mdi-information</v-icon>
                      {{ filteredDataForExport.length }} Einträge gefunden
                    </v-chip>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
        </div>
      </v-expand-transition>

      <v-divider v-show="showFilters" class="card-divider"></v-divider>

      <!-- Verbesserte Tabelle mit modernem Design und direkten Aktionsbuttons -->
      <v-data-table
        :headers="headers"
        :items="displayedInstandsetzungen"
        density="comfortable"
        class="data-table"
        :items-per-page="10"
        :items-per-page-options="[5, 10, 20, 50]"
      >
        <template v-slot:item="{ item }">
          <tr>
            <td>
              <div class="date-cell" @click="showFullDate(item.erstelldatum)">
                {{ formatDate(item.erstelldatum) }}
                <v-tooltip activator="parent" location="top">
                  {{ formatDateTime(item.erstelldatum) }}
                </v-tooltip>
              </div>
            </td>

            <td>
              <div class="auftrags-cell">
                {{ item.auftragsnummer }}
              </div>
            </td>

            <td>
              <v-chip
                size="small"
                class="typ-chip geraete-chip"
                color="success-lighten-5"
              >
                {{ getTypName(item.geraetetyp_id, "Gerätetyp") }}
              </v-chip>
            </td>

            <td>
              <v-chip
                size="small"
                class="typ-chip reparatur-chip"
                color="success-lighten-5"
              >
                {{ getTypName(item.reparaturtyp_id, "Reparaturtyp") }}
              </v-chip>
            </td>

            <td>
              <div class="menge-cell">
                {{ item.menge }}
              </div>
            </td>

            <td>
              <div class="kommentar-cell">
                {{ truncateText(item.kommentar, 20) }}
                <v-tooltip
                  v-if="item.kommentar && item.kommentar.length > 20"
                  activator="parent"
                  location="top"
                  max-width="300"
                >
                  {{ item.kommentar }}
                </v-tooltip>
              </div>
            </td>

            <td class="action-buttons-cell">
              <div class="action-menu-wrapper">
                <v-menu
                  location="bottom"
                  :close-on-content-click="true"
                  transition="scale-transition"
                  offset="5"
                >
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      size="small"
                      v-bind="props"
                      variant="text"
                      class="action-menu-btn"
                      color="grey-lighten-1"
                    >
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>

                  <v-list density="compact" class="action-menu-list">
                    <v-list-item
                      @click="editInstandsetzung(item)"
                      prepend-icon="mdi-pencil"
                      title="Bearbeiten"
                      class="action-menu-item edit-action"
                      value="edit"
                    ></v-list-item>

                    <v-divider class="menu-divider"></v-divider>

                    <v-list-item
                      @click="confirmDelete(item.id)"
                      prepend-icon="mdi-delete"
                      title="Löschen"
                      class="action-menu-item delete-action"
                      value="delete"
                    ></v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </td>
          </tr>
        </template>

        <!-- Leerstand-Anzeige -->
        <template v-slot:no-data>
          <div class="no-data-message">
            <v-icon size="large" color="grey" class="mb-3"
              >mdi-database-off</v-icon
            >
            <p>Keine Instandsetzungen vorhanden</p>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- KV-Bestätigung gefunden Dialog -->
    <v-dialog v-model="kvConfirmDialog" max-width="600" persistent class="kv-confirm-dialog">
      <v-card v-if="foundKv" class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon :color="isOwnKv ? 'warning' : 'info'" size="28" class="mr-3">mdi-file-document-alert</v-icon>
          <span class="text-h6">KV-Bestätigung gefunden!</span>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-6">
          <!-- Info Alert für eigene KV -->
          <v-alert
            v-if="isOwnKv"
            color="warning"
            variant="tonal"
            class="mb-4"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-3" size="32">mdi-information</v-icon>
              <div>
                <div class="font-weight-bold mb-1">Diese Nummer ist eine offene KV-Bestätigung für dich!</div>
                <div class="text-caption">KV-Nummer: {{ foundKv.kv_nummer }}</div>
              </div>
            </div>
          </v-alert>

          <!-- Info Alert für fremde KV -->
          <v-alert
            v-else
            color="info"
            variant="tonal"
            class="mb-4"
          >
            <div class="d-flex align-center">
              <v-icon class="mr-3" size="32">mdi-account-switch</v-icon>
              <div>
                <div class="font-weight-bold mb-1">Diese KV ist einem anderen Monteur zugewiesen!</div>
                <div class="text-caption">KV-Nummer: {{ foundKv.kv_nummer }}</div>
                <div class="text-caption mt-1">
                  Aktuell zugewiesen an: {{ foundKv.monteur_vorname }} {{ foundKv.monteur_nachname }}
                  <span v-if="foundKv.monteur_nummer"> (Nr. {{ foundKv.monteur_nummer }})</span>
                </div>
              </div>
            </div>
          </v-alert>

          <!-- KV Details -->
          <div class="kv-details-card pa-4 mb-4">
            <v-row dense>
              <v-col cols="6">
                <div class="detail-label">Status</div>
                <v-chip :color="getKvStatusColor(foundKv.status)" size="small" variant="flat">
                  {{ getKvStatusText(foundKv.status) }}
                </v-chip>
              </v-col>

              <v-col cols="6">
                <div class="detail-label">Bearbeiter</div>
                <div class="detail-value">{{ foundKv.bearbeiter_vorname }} {{ foundKv.bearbeiter_nachname }}</div>
              </v-col>

              <v-col cols="12" v-if="foundKv.bearbeiter_notiz">
                <div class="detail-label">Notiz vom Bearbeiter</div>
                <v-alert color="info" variant="tonal" density="compact" class="mt-2">
                  {{ foundKv.bearbeiter_notiz }}
                </v-alert>
              </v-col>
            </v-row>
          </div>

          <!-- Optionen für eigene KV -->
          <div v-if="isOwnKv">
            <div class="text-h6 mb-3">Was möchtest du tun?</div>

            <v-list class="options-list">
              <v-list-item
                prepend-icon="mdi-check-circle"
                title="KV nur abschließen"
                subtitle="KV als erledigt markieren ohne Instandsetzungseintrag"
                class="option-item"
                @click="completeKvDirectly"
                :disabled="kvCompleting"
              />

              <v-divider class="my-2" />

              <v-list-item
                prepend-icon="mdi-wrench-check"
                title="KV abschließen + Instandsetzung eintragen"
                subtitle="KV erledigen und gleichzeitig in Instandsetzung eintragen"
                class="option-item option-item-primary"
                @click="completeKvWithInstandsetzung"
                :disabled="kvCompleting"
              />

              <v-divider class="my-2" />

              <v-list-item
                prepend-icon="mdi-arrow-right"
                title="KV ignorieren, nur Instandsetzung"
                subtitle="KV bleibt offen, nur normale Instandsetzung erstellen"
                class="option-item"
                @click="cancelKvDialog"
                :disabled="kvCompleting"
              />
            </v-list>
          </div>

          <!-- Optionen für fremde KV -->
          <div v-else>
            <div class="text-h6 mb-3">Möchtest du diese KV übernehmen?</div>

            <v-alert
              color="warning"
              variant="tonal"
              density="compact"
              class="mb-4"
            >
              <div class="text-caption">
                <v-icon size="small" class="mr-1">mdi-alert</v-icon>
                Wenn du die KV übernimmst, wird sie dir zugewiesen und der bisherige Monteur erhält eine Benachrichtigung.
              </div>
            </v-alert>

            <v-list class="options-list">
              <v-list-item
                prepend-icon="mdi-account-arrow-right"
                title="KV übernehmen + abschließen"
                subtitle="KV übernehmen, als erledigt markieren (ohne Instandsetzung)"
                class="option-item"
                @click="takeoverAndComplete"
                :disabled="kvCompleting"
              />

              <v-divider class="my-2" />

              <v-list-item
                prepend-icon="mdi-wrench-check"
                title="KV übernehmen + Instandsetzung eintragen"
                subtitle="KV übernehmen, abschließen und in Instandsetzung eintragen"
                class="option-item option-item-primary"
                @click="completeKvWithInstandsetzung"
                :disabled="kvCompleting"
              />

              <v-divider class="my-2" />

              <v-list-item
                prepend-icon="mdi-close"
                title="KV nicht übernehmen"
                subtitle="Nur normale Instandsetzung erstellen, KV bleibt beim anderen Monteur"
                class="option-item"
                @click="cancelKvDialog"
                :disabled="kvCompleting"
              />
            </v-list>
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn
            color="grey"
            variant="text"
            @click="kvConfirmDialog = false; foundKv = null; isOwnKv = true; resetForm();"
            :disabled="kvCompleting"
          >
            Abbrechen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog zur Auswahl von Gerätetyp und Reparaturtyp -->
    <v-dialog v-model="typeDialog" max-width="500" class="type-dialog">
      <v-card
        class="dialog-card"
        tabindex="0"
        @keydown.enter.stop.prevent="handleDialogEnter"
        @keydown.esc.stop.prevent="typeDialog = false"
        @keydown.tab="handleTabNavigation"
      >
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-devices</v-icon>
          Gerätetyp & Reparaturtyp wählen
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <!-- Ersetze die Chip-Groups mit Radio-Buttons im Type-Dialog -->
          <div class="selection-container mb-4">
            <h4 class="selection-title d-flex align-center">
              <v-icon color="success" size="small" class="mr-2"
                >mdi-laptop</v-icon
              >
              Gerätetyp
            </h4>

            <div class="radio-options">
              <v-radio-group v-model="geraetetyp" mandatory inline>
                <v-radio
                  v-for="item in geraetetypen"
                  :key="item.id"
                  :value="item.id"
                  :label="item.name"
                  color="success"
                  class="type-radio"
                ></v-radio>
              </v-radio-group>
            </div>
          </div>

          <v-divider class="selection-divider my-4"></v-divider>

          <div class="selection-container">
            <h4 class="selection-title d-flex align-center">
              <v-icon color="success" size="small" class="mr-2"
                >mdi-wrench-outline</v-icon
              >
              Reparaturtyp
            </h4>

            <div class="radio-options">
              <v-radio-group v-model="reparaturtyp" mandatory inline>
                <v-radio
                  v-for="item in reparaturtypen"
                  :key="item.id"
                  :value="item.id"
                  :label="item.name"
                  color="success"
                  class="type-radio"
                ></v-radio>
              </v-radio-group>
            </div>
          </div>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="typeDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            :color="geraetetyp && reparaturtyp ? 'success' : 'grey-darken-1'"
            variant="elevated"
            :disabled="!geraetetyp || !reparaturtyp"
            @click="confirmTypeSelection"
          >
            <v-icon start>mdi-check</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bearbeiten-Dialog -->
    <v-dialog v-model="editDialog" max-width="550" class="edit-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-pencil-box</v-icon>
          Instandsetzung bearbeiten
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <v-row>
            <!-- Ersetze die Chip-Groups mit Radio-Buttons im Edit-Dialog -->
            <v-col cols="12">
              <div class="selection-container mb-3">
                <h4 class="selection-title d-flex align-center">
                  <v-icon color="success" size="small" class="mr-2"
                    >mdi-laptop</v-icon
                  >
                  Gerätetyp
                </h4>
                <v-radio-group
                  v-model="editItem.geraetetyp_id"
                  mandatory
                  inline
                >
                  <v-radio
                    v-for="item in geraetetypen"
                    :key="item.id"
                    :value="item.id"
                    :label="item.name"
                    color="success"
                    class="type-radio"
                  ></v-radio>
                </v-radio-group>
              </div>
            </v-col>

            <v-col cols="12">
              <div class="selection-container mb-3">
                <h4 class="selection-title d-flex align-center">
                  <v-icon color="success" size="small" class="mr-2"
                    >mdi-wrench-outline</v-icon
                  >
                  Reparaturtyp
                </h4>
                <v-radio-group
                  v-model="editItem.reparaturtyp_id"
                  mandatory
                  inline
                >
                  <v-radio
                    v-for="item in reparaturtypen"
                    :key="item.id"
                    :value="item.id"
                    :label="item.name"
                    color="success"
                    class="type-radio"
                  ></v-radio>
                </v-radio-group>
              </div>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editItem.auftragsnummer"
                label="Auftragsnummer"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                maxlength="10"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-identifier</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="editItem.menge"
                type="number"
                label="Menge"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small">mdi-counter</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="editItem.kommentar"
                label="Kommentar"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                auto-grow
                rows="2"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small"
                    >mdi-comment-text-outline</v-icon
                  >
                </template>
              </v-textarea>
            </v-col>

            <v-col cols="12">
              <v-text-field
                v-model="editItem.erstelldatum"
                label="Datum und Uhrzeit"
                type="datetime-local"
                variant="outlined"
                bg-color="rgba(30, 30, 40, 0.5)"
                class="form-field"
                color="success"
              >
                <template v-slot:prepend-inner>
                  <v-icon color="success" size="small"
                    >mdi-calendar-clock</v-icon
                  >
                </template>
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="editDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            variant="elevated"
            @click="updateInstandsetzung"
          >
            <v-icon start>mdi-content-save</v-icon>
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Löschen-Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400" class="delete-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title warning-header d-flex align-center">
          <v-icon color="error" size="24" class="mr-2">mdi-alert-circle</v-icon>
          Löschen bestätigen
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content text-center pa-6">
          <p>Bist du sicher, dass du diesen Eintrag löschen möchtest?</p>
          <p class="text-caption mt-2">
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-actions class="dialog-actions">
          <v-btn variant="text" color="grey" @click="deleteDialog = false">
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="elevated" @click="deleteInstandsetzung">
            <v-icon start>mdi-delete</v-icon>
            Löschen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Verbesserter Suchdialog -->
    <v-dialog v-model="instSearchDialog" max-width="800" class="search-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-magnify</v-icon>
          Instandsetzungen durchsuchen
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="instSearchDialog = false"
          ></v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <!-- Filter-Zeile -->
          <v-row dense class="mb-3">
            <v-col cols="12" sm="4">
              <v-select
                v-model="searchDialogMonth"
                :items="monthOptions"
                label="Monat"
                clearable
                density="compact"
                variant="outlined"
                prepend-inner-icon="mdi-calendar-month"
                hide-details
                color="success"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="searchDialogGeraetetyp"
                :items="geraetetypFilterOptions"
                label="Gerätetyp"
                clearable
                density="compact"
                variant="outlined"
                prepend-inner-icon="mdi-devices"
                hide-details
                color="success"
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <v-select
                v-model="searchDialogReparaturtyp"
                :items="reparaturtypFilterOptions"
                label="Reparaturtyp"
                clearable
                density="compact"
                variant="outlined"
                prepend-inner-icon="mdi-tools"
                hide-details
                color="success"
              ></v-select>
            </v-col>
          </v-row>

          <v-text-field
            v-model="instSearchQuery"
            label="Suchbegriff eingeben... (optional)"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            clearable
            hide-details
            class="search-field mb-4"
            prepend-inner-icon="mdi-magnify"
            @keyup.esc="instSearchDialog = false"
            color="success"
          ></v-text-field>

          <!-- Suchergebnisse -->
          <div v-if="hasActiveSearch" class="search-results">
            <v-data-table
              :headers="headers"
              :items="filteredInstandsetzungen"
              density="comfortable"
              class="data-table search-table"
              hide-default-footer
            >
              <template v-slot:item="{ item }">
                <tr>
                  <td>
                    <div
                      class="date-cell"
                      @click="showFullDate(item.erstelldatum)"
                    >
                      {{ formatDate(item.erstelldatum) }}
                      <v-tooltip activator="parent" location="top">
                        {{ formatDateTime(item.erstelldatum) }}
                      </v-tooltip>
                    </div>
                  </td>

                  <td>
                    <div class="auftrags-cell">
                      {{ item.auftragsnummer }}
                    </div>
                  </td>

                  <td>
                    <v-chip
                      size="small"
                      class="typ-chip geraete-chip"
                      color="success-lighten-5"
                    >
                      {{ getTypName(item.geraetetyp_id, "Gerätetyp") }}
                    </v-chip>
                  </td>

                  <td>
                    <v-chip
                      size="small"
                      class="typ-chip reparatur-chip"
                      color="success-lighten-5"
                    >
                      {{ getTypName(item.reparaturtyp_id, "Reparaturtyp") }}
                    </v-chip>
                  </td>

                  <td>
                    <div class="menge-cell">
                      {{ item.menge }}
                    </div>
                  </td>

                  <td>
                    <div class="kommentar-cell">
                      {{ truncateText(item.kommentar, 20) }}
                      <v-tooltip
                        v-if="item.kommentar && item.kommentar.length > 20"
                        activator="parent"
                        location="top"
                        max-width="300"
                      >
                        {{ item.kommentar }}
                      </v-tooltip>
                    </div>
                  </td>

                  <td class="action-buttons-cell">
                    <div class="action-menu-wrapper">
                      <v-menu
                        location="bottom"
                        :close-on-content-click="true"
                        transition="scale-transition"
                        offset="5"
                      >
                        <template v-slot:activator="{ props }">
                          <v-btn
                            icon
                            size="small"
                            v-bind="props"
                            variant="text"
                            class="action-menu-btn"
                            color="grey-lighten-1"
                          >
                            <v-icon>mdi-dots-vertical</v-icon>
                          </v-btn>
                        </template>

                        <v-list density="compact" class="action-menu-list">
                          <v-list-item
                            @click="editInstandsetzung(item)"
                            prepend-icon="mdi-pencil"
                            title="Bearbeiten"
                            class="action-menu-item edit-action"
                            value="edit"
                          ></v-list-item>

                          <v-divider class="menu-divider"></v-divider>

                          <v-list-item
                            @click="confirmDelete(item.id)"
                            prepend-icon="mdi-delete"
                            title="Löschen"
                            class="action-menu-item delete-action"
                            value="delete"
                          ></v-list-item>
                        </v-list>
                      </v-menu>
                    </div>
                  </td>
                </tr>
              </template>
            </v-data-table>

            <!-- Keine Suchergebnisse -->
            <div
              v-if="filteredInstandsetzungen.length === 0"
              class="no-results"
            >
              <v-icon size="large" color="grey" class="mb-2"
                >mdi-file-search-outline</v-icon
              >
              <p>Keine passenden Einträge gefunden</p>
            </div>
          </div>

          <!-- Initiale Ansicht (keine Filter aktiv) -->
          <div v-else class="search-placeholder">
            <v-icon size="x-large" color="grey" class="mb-4"
              >mdi-filter-search</v-icon
            >
            <p>Wähle einen Filter oder gib einen Suchbegriff ein</p>
            <p class="text-caption mt-2">
              Filtern nach Monat, Gerätetyp, Reparaturtyp oder freier Texteingabe
            </p>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-chip
            v-if="hasActiveSearch"
            color="success"
            variant="tonal"
            size="small"
          >
            <v-icon start size="small">mdi-information</v-icon>
            {{ filteredInstandsetzungen.length }} Einträge
          </v-chip>
          <v-spacer></v-spacer>
          <v-btn
            v-if="hasActiveSearch && filteredInstandsetzungen.length > 0"
            color="success"
            variant="tonal"
            prepend-icon="mdi-download"
            size="small"
            class="mr-2"
            @click="downloadSearchCSV"
          >
            CSV Export
          </v-btn>
          <v-btn color="grey" variant="text" @click="instSearchDialog = false">
            <v-icon start>mdi-close</v-icon>
            Schließen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- IRIS Code Matrix Dialog -->
    <v-dialog v-model="irisMatrixDialog" max-width="1000" class="iris-matrix-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-table-search</v-icon>
          IRIS Code Matrix
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="irisMatrixDialog = false"
          ></v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content py-4">
          <v-text-field
            v-model="irisSearchCode"
            label="Fehlercode suchen (z.B. G07)"
            variant="outlined"
            bg-color="rgba(30, 30, 40, 0.5)"
            class="form-field mb-4"
            color="success"
            clearable
            prepend-inner-icon="mdi-magnify"
          ></v-text-field>

          <div class="matrix-table-wrapper">
            <table class="iris-matrix-table">
              <thead>
                <tr>
                  <th class="sticky-col left">Bereich</th>
                  <th class="sticky-col left2">Fehlercode</th>
                  <th v-for="col in errorTypes" :key="col" class="sticky-header">{{ col }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, rowIdx) in filteredIrisRows" :key="row.code" :class="{ 'zebra': rowIdx % 2 === 1 }">
                  <td class="sticky-col left">{{ row.section }}</td>
                  <td class="sticky-col left2">{{ row.code }}</td>
                  <td v-for="col in errorTypes" :key="col" :class="{ 'matrix-x': row.types.includes(col) }">
                    <v-tooltip v-if="row.types.includes(col)" location="top">
                      <template #activator="{ props }">
                        <span v-bind="props" class="matrix-x-pill">X</span>
                      </template>
                      <span>{{ errorTypeLegend[col] }}</span>
                    </v-tooltip>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="legend-container mt-8">
            <h3 class="legend-title">Legende Fehlerarten</h3>
            <div class="legend-grid">
              <div v-for="col in errorTypes" :key="col" class="legend-row">
                <span class="legend-key">{{ col }}</span>
                <span class="legend-desc">{{ errorTypeLegend[col] }}</span>
              </div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Spare Parts Dialog -->
    <v-dialog v-model="sparePartsDialog" max-width="1200" class="spare-parts-dialog">
      <v-card class="dialog-card">
        <v-card-title class="dialog-title d-flex align-center">
          <v-icon color="success" size="24" class="mr-2">mdi-barcode</v-icon>
          Ersatzteile
          <v-spacer></v-spacer>
          <v-btn
            icon="mdi-close"
            variant="text"
            @click="sparePartsDialog = false"
          ></v-btn>
        </v-card-title>

        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-content pa-0">
          <SparePartsView :embedded="true" />
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Barcode Scanner Dialog -->
    <v-dialog v-model="showScanner" max-width="400">
      <v-card class="scanner-card">
        <v-card-title class="text-h6 text-center pa-4">
          <v-icon class="mr-2">mdi-qrcode-scan</v-icon>
          Barcode Scanner
        </v-card-title>

        <v-card-text class="pa-4">
          <div class="scanner-container">
            <div v-if="!scannerReady && !scannerError" class="text-center">
              <v-progress-circular indeterminate color="success" size="50"></v-progress-circular>
              <p class="mt-3">Kamera wird initialisiert...</p>
            </div>

            <div v-if="scannerError" class="text-center error-container">
              <v-icon color="error" size="48">mdi-camera-off</v-icon>
              <p class="mt-2 text-error">{{ scannerError }}</p>
              <v-btn
                color="primary"
                variant="text"
                size="small"
                @click="openBarcodeScanner"
                class="mt-2"
              >
                Nochmal versuchen
              </v-btn>
            </div>

            <div v-if="!scannerError" class="camera-container">
              <video
                id="scanner-video"
                ref="videoElement"
                class="scanner-video"
                autoplay
                playsinline
                muted
                webkit-playsinline="true"
                :style="{ visibility: scannerReady ? 'visible' : 'hidden' }"
              ></video>
              <div v-if="scannerReady" class="scanner-overlay">
                <div class="scanner-frame"></div>
              </div>
            </div>

            <!-- Manuelle Eingabe falls automatisches Scannen nicht funktioniert -->
            <v-text-field
              v-model="scannedValue"
              label="Code eingeben oder scannen"
              variant="outlined"
              class="mt-3"
              color="success"
              maxlength="10"
              @keypress="validateNumericInput"
              hint="Gebe die Auftragsnummer manuell ein oder halte einen Barcode vor die Kamera"
              persistent-hint
            >
              <template v-slot:prepend-inner>
                <v-icon color="success" size="small">mdi-keyboard</v-icon>
              </template>
            </v-text-field>
          </div>
        </v-card-text>

        <v-card-actions class="pa-4">
          <v-btn
            color="error"
            variant="text"
            @click="closeBarcodeScanner"
          >
            Abbrechen
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            variant="elevated"
            @click="useScanResult"
            :disabled="!scannedValue"
          >
            Verwenden
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import axios from "axios";
import { useAuthStore } from "@/stores/auth";
import { API_BASE_URL } from "../config";
import { useToast } from "vue-toastification";
import SparePartsView from "@/views/SparePartsView.vue";
import Quagga from "@ericblade/quagga2";

// ======== Basis-Setup ========
const toast = useToast();
const authStore = useAuthStore();

// Neue Referenzen für DOM-Elemente
const auftragsnummerField = ref(null);
const videoElement = ref(null);

// ======== Barcode Scanner Setup ========
const showScanner = ref(false);
const scannerReady = ref(false);
const scannerError = ref("");
const scannedValue = ref("");
const mediaStream = ref(null);

// Mobile Detection
const isMobileDevice = computed(() => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
});

const isIOS = computed(() => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
});

// ======== Zustandsverwaltung ========
// Form-Zustände
const auftragsnummer = ref("");
const menge = ref(1);
const kommentar = ref("");
const geraetetyp = ref(null);
const reparaturtyp = ref(null);
const formSubmitting = ref(false);

// Listen-Zustände
const instandsetzungen = ref([]);
const geraetetypen = ref([]);
const reparaturtypen = ref([]);
const loading = ref({
  initial: true,
  fetching: false,
  submitting: false,
  updating: false,
  deleting: false,
  validating: false,
});

// UI-Zustände
const isListMaximized = ref(false);
const actionsVisible = ref(false);

// Dialog-Zustände
const typeDialog = ref(false);
const editDialog = ref(false);
const deleteDialog = ref(false);
const instSearchDialog = ref(false);
const irisMatrixDialog = ref(false);
const sparePartsDialog = ref(false);
const kvConfirmDialog = ref(false);

// KV-Bestätigungs-Zustände
const foundKv = ref(null);
const kvCompleting = ref(false);
const isOwnKv = ref(true); // Ist die KV dem aktuellen Monteur zugewiesen?

// Such- und Bearbeiten-Zustände
const instSearchQuery = ref("");
const deleteId = ref(null);
const editItem = ref({});

// Filter-Zustände (Haupttabelle)
const showFilters = ref(false);
const filterMonth = ref(null);
const filterGeraetetyp = ref(null);
const filterReparaturtyp = ref(null);

// Such-Dialog-Filter-Zustände
const searchDialogMonth = ref(null);
const searchDialogGeraetetyp = ref(null);
const searchDialogReparaturtyp = ref(null);

// IRIS Matrix Zustände
const irisSearchCode = ref('');

// Validierung für numerische Eingabe - nur Zahlen erlauben
const validateNumericInput = (event) => {
  // Blockiere alle nicht-numerischen Zeichen (inklusive Leerzeichen)
  if (!/[0-9]/.test(event.key)) {
    event.preventDefault();
  }
};

// ======== Computed Properties ========
// Tabellenheader basierend auf sichtbaren Spalten
const headers = computed(() => [
  { title: "Datum", key: "erstelldatum", align: "start", sortable: true },
  {
    title: "Auftragsnummer",
    key: "auftragsnummer",
    align: "start",
    sortable: true,
  },
  { title: "Gerätetyp", key: "geraetetyp_id", align: "start", sortable: true },
  {
    title: "Reparaturtyp",
    key: "reparaturtyp_id",
    align: "start",
    sortable: true,
  },
  { title: "Menge", key: "menge", align: "center", sortable: true },
  { title: "Kommentar", key: "kommentar", align: "start", sortable: false },
  { title: "Aktionen", key: "actions", align: "center", sortable: false },
]);

// Dynamische Header basierend auf aktionsvisibilität
const computedHeaders = computed(() => {
  return actionsVisible.value
    ? headers.value
    : headers.value.filter((header) => header.key !== "actions");
});

// Gefilterte Instandsetzungen für die Suchfunktion (Such-Dialog)
const filteredInstandsetzungen = computed(() => {
  const hasText = instSearchQuery.value?.trim();
  const hasMonth = searchDialogMonth.value;
  const hasGeraete = searchDialogGeraetetyp.value;
  const hasReparatur = searchDialogReparaturtyp.value;

  if (!hasText && !hasMonth && !hasGeraete && !hasReparatur) return [];

  let result = instandsetzungen.value;

  // Monats-Filter
  if (hasMonth) {
    result = result.filter((item) => {
      const itemDate = new Date(item.erstelldatum);
      const itemMonth = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;
      return itemMonth === searchDialogMonth.value;
    });
  }

  // Gerätetyp-Filter
  if (hasGeraete) {
    result = result.filter((item) => item.geraetetyp_id == searchDialogGeraetetyp.value);
  }

  // Reparaturtyp-Filter
  if (hasReparatur) {
    result = result.filter((item) => item.reparaturtyp_id == searchDialogReparaturtyp.value);
  }

  // Text-Suche
  if (hasText) {
    const query = hasText.toLowerCase();
    result = result.filter((item) => {
      if (item.auftragsnummer?.toLowerCase().includes(query)) return true;
      if (item.kommentar?.toLowerCase().includes(query)) return true;
      if (getTypName(item.geraetetyp_id, "Gerätetyp").toLowerCase().includes(query)) return true;
      if (getTypName(item.reparaturtyp_id, "Reparaturtyp").toLowerCase().includes(query)) return true;
      if (formatDateTime(item.erstelldatum).toLowerCase().includes(query)) return true;
      return false;
    });
  }

  return result;
});

// Computed Properties für Filter-Optionen
const monthOptions = computed(() => {
  const months = [];
  const now = new Date();

  // Letzten 12 Monate generieren
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const monthYear = date.toLocaleDateString('de-DE', { year: 'numeric', month: 'long' });
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    months.push({ title: monthYear, value });
  }

  return months;
});

const geraetetypFilterOptions = computed(() => {
  return geraetetypen.value.map(typ => ({
    title: typ.name,
    value: typ.id
  }));
});

const reparaturtypFilterOptions = computed(() => {
  return reparaturtypen.value.map(typ => ({
    title: typ.name,
    value: typ.id
  }));
});

// Gefilterte Daten für Export
const filteredDataForExport = computed(() => {
  if (!filterMonth.value) return [];

  return instandsetzungen.value.filter(item => {
    // Monat-Filter
    const itemDate = new Date(item.erstelldatum);
    const itemMonth = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}`;

    if (itemMonth !== filterMonth.value) return false;

    // Gerätetyp-Filter (optional) - lose Gleichheit wegen möglicher Typ-Unterschiede
    if (filterGeraetetyp.value && item.geraetetyp_id != filterGeraetetyp.value) {
      return false;
    }

    // Reparaturtyp-Filter (optional) - lose Gleichheit wegen möglicher Typ-Unterschiede
    if (filterReparaturtyp.value && item.reparaturtyp_id != filterReparaturtyp.value) {
      return false;
    }

    return true;
  });
});

// Angezeigte Instandsetzungen in der Tabelle (entweder gefiltert oder alle)
const displayedInstandsetzungen = computed(() => {
  // Wenn ein Monat-Filter aktiv ist, zeige gefilterte Daten
  if (filterMonth.value) {
    return filteredDataForExport.value;
  }
  // Ansonsten zeige alle Instandsetzungen
  return instandsetzungen.value;
});

// ======== Export-Funktionen ========
const printFilteredData = () => {
  if (filteredDataForExport.value.length === 0) return;

  const monthLabel = monthOptions.value.find(m => m.value === filterMonth.value)?.title || filterMonth.value;

  let printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Instandsetzungen - ${monthLabel}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h1 { color: #43a047; border-bottom: 2px solid #43a047; padding-bottom: 10px; }
        h2 { color: #666; font-size: 16px; margin: 10px 0; }
        table { width: 100%; border-collapse: collapse; margin-top: 20px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #43a047; color: white; }
        tr:nth-child(even) { background-color: #f2f2f2; }
        .header-info { margin: 20px 0; }
        .summary { margin: 20px 0; padding: 10px; background: #f5f5f5; border-left: 4px solid #43a047; }
      </style>
    </head>
    <body>
      <h1>Instandsetzungen - ${monthLabel}</h1>
      <div class="header-info">
        <p><strong>Mitarbeiter:</strong> ${authStore.user?.vorname} ${authStore.user?.nachname} (Nr. ${authStore.user?.mitarbeiternummer})</p>
        <p><strong>Erstellt am:</strong> ${new Date().toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
  `;

  if (filterGeraetetyp.value) {
    const geraetetypName = geraetetypen.value.find(g => g.id === filterGeraetetyp.value)?.name;
    printContent += `<p><strong>Gerätetyp-Filter:</strong> ${geraetetypName}</p>`;
  }

  if (filterReparaturtyp.value) {
    const reparaturtypName = reparaturtypen.value.find(r => r.id === filterReparaturtyp.value)?.name;
    printContent += `<p><strong>Reparaturtyp-Filter:</strong> ${reparaturtypName}</p>`;
  }

  printContent += `
      </div>
      <div class="summary">
        <strong>Gesamtanzahl:</strong> ${filteredDataForExport.value.length} Einträge
      </div>
      <table>
        <thead>
          <tr>
            <th>Datum</th>
            <th>Auftragsnummer</th>
            <th>Gerätetyp</th>
            <th>Reparaturtyp</th>
            <th>Menge</th>
            <th>Kommentar</th>
          </tr>
        </thead>
        <tbody>
  `;

  filteredDataForExport.value.forEach(item => {
    printContent += `
      <tr>
        <td>${formatDate(item.erstelldatum)}</td>
        <td>${item.auftragsnummer || '—'}</td>
        <td>${getTypName(item.geraetetyp_id, 'Gerätetyp')}</td>
        <td>${getTypName(item.reparaturtyp_id, 'Reparaturtyp')}</td>
        <td>${item.menge}</td>
        <td>${item.kommentar || '—'}</td>
      </tr>
    `;
  });

  printContent += `
        </tbody>
      </table>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  printWindow.document.write(printContent);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 250);
};

const downloadCSV = () => {
  if (filteredDataForExport.value.length === 0) return;

  const monthLabel = monthOptions.value.find(m => m.value === filterMonth.value)?.title || filterMonth.value;

  // CSV Header
  let csv = 'Datum;Auftragsnummer;Gerätetyp;Reparaturtyp;Menge;Kommentar\n';

  // CSV Rows
  filteredDataForExport.value.forEach(item => {
    const row = [
      formatDate(item.erstelldatum),
      item.auftragsnummer || '',
      getTypName(item.geraetetyp_id, 'Gerätetyp'),
      getTypName(item.reparaturtyp_id, 'Reparaturtyp'),
      item.menge,
      (item.kommentar || '').replace(/;/g, ',').replace(/\n/g, ' ')
    ];
    csv += row.join(';') + '\n';
  });

  // BOM für UTF-8 hinzufügen (wichtig für Excel)
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });

  // Download-Link erstellen
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  const fileName = `Instandsetzungen_${monthLabel.replace(/ /g, '_')}_${authStore.user?.mitarbeiternummer || 'export'}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast.success(`CSV-Datei "${fileName}" wurde heruntergeladen`);
};

// CSV-Export aus dem Such-Dialog
const downloadSearchCSV = () => {
  if (filteredInstandsetzungen.value.length === 0) return;

  const monthLabel = searchDialogMonth.value
    ? monthOptions.value.find(m => m.value === searchDialogMonth.value)?.title || searchDialogMonth.value
    : null;

  const geraeteName = searchDialogGeraetetyp.value
    ? geraetetypen.value.find(g => g.id == searchDialogGeraetetyp.value)?.name || ''
    : null;

  const reparaturName = searchDialogReparaturtyp.value
    ? reparaturtypen.value.find(r => r.id == searchDialogReparaturtyp.value)?.name || ''
    : null;

  let csv = 'Datum;Auftragsnummer;Gerätetyp;Reparaturtyp;Menge;Kommentar\n';

  filteredInstandsetzungen.value.forEach(item => {
    const row = [
      formatDate(item.erstelldatum),
      item.auftragsnummer || '',
      getTypName(item.geraetetyp_id, 'Gerätetyp'),
      getTypName(item.reparaturtyp_id, 'Reparaturtyp'),
      item.menge,
      (item.kommentar || '').replace(/;/g, ',').replace(/\n/g, ' ')
    ];
    csv += row.join(';') + '\n';
  });

  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  const parts = [
    'Instandsetzungen',
    monthLabel,
    geraeteName,
    reparaturName,
    authStore.user?.mitarbeiternummer || 'export'
  ].filter(Boolean);

  const fileName = `${parts.join('_').replace(/ /g, '_')}.csv`;

  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast.success(`CSV-Datei "${fileName}" wurde heruntergeladen`);
};

// Computed: Ist irgendein Such-Dialog-Filter aktiv?
const hasActiveSearch = computed(() =>
  !!(instSearchQuery.value?.trim() || searchDialogMonth.value || searchDialogGeraetetyp.value || searchDialogReparaturtyp.value)
);

// Verbesserte Tastaturnavigation im Dialog
const handleTabNavigation = (e) => {
  // Verhindert, dass der Tab-Fokus den Dialog verlässt
  const focusableElements = document
    .querySelector(".type-dialog")
    .querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (e.shiftKey && document.activeElement === firstElement) {
    e.preventDefault();
    lastElement.focus();
  } else if (!e.shiftKey && document.activeElement === lastElement) {
    e.preventDefault();
    firstElement.focus();
  }
};

// FormValidation-Zustand
const formValid = computed(() => {
  return (
    auftragsnummer.value.length >= 7 && auftragsnummer.value.length <= 10 && menge.value > 0 && menge.value <= 100
  );
});

// ======== UI Funktionen ========
// Toggle-Funktionen für UI-Elemente
const toggleActionsVisibility = () => {
  actionsVisible.value = !actionsVisible.value;
};

// Text-Hilfsfunktionen
const truncateText = (text, length) => {
  if (!text) return "—";
  if (text.length <= length) return text;
  return text.substring(0, length) + "...";
};

// ======== Datum-Funktionen ========
// Formatierungsfunktionen für Datum und Uhrzeit
const formatDateTime = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Ungültiges Datum";

    // Deutsches Datumsformat mit Uhrzeit
    return date.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (e) {
    console.error("Datum-Formatierungsfehler:", e);
    return "Fehler beim Formatieren";
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Ungültiges Datum";

    // Deutsches Datumsformat
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (e) {
    console.error("Datum-Formatierungsfehler:", e);
    return "Fehler beim Formatieren";
  }
};

// Detail-Ansicht für Datum
const showFullDate = (dateString) => {
  toast.info("Datum/Uhrzeit: " + formatDateTime(dateString));
};

// ======== Barcode Scanner Methoden ========
const openBarcodeScanner = async () => {
  showScanner.value = true;
  scannerReady.value = false;
  scannerError.value = "";
  scannedValue.value = "";

  // Warten bis das DOM bereit ist
  await nextTick();

  try {
    // Kamera-Berechtigung anfordern
    const constraints = isIOS.value ? {
      video: {
        facingMode: { exact: 'environment' },
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 }
      }
    } : {
      video: {
        facingMode: 'environment',
        width: { ideal: 640 },
        height: { ideal: 480 }
      }
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    mediaStream.value = stream;

    // Barcode-Detection starten
    const delay = isMobileDevice.value ? 1000 : 500;
    setTimeout(async () => {
      await startBarcodeDetection();
    }, delay);  } catch (error) {
    console.error('Kamera-Zugriff fehlgeschlagen:', error);

    if (error.name === 'NotAllowedError') {
      scannerError.value = 'Kamera-Zugriff wurde verweigert. Bitte erlaube den Zugriff in den Browser-Einstellungen und lade die Seite neu.';
    } else if (error.name === 'NotFoundError') {
      scannerError.value = 'Keine Kamera gefunden. Stelle sicher, dass dein Gerät eine Kamera hat.';
    } else if (error.name === 'NotReadableError') {
      scannerError.value = 'Kamera wird bereits von einer anderen Anwendung verwendet.';
    } else {
      scannerError.value = 'Fehler beim Zugriff auf die Kamera: ' + error.message;
    }
  }
};

const startBarcodeDetection = async () => {
  // Video-Element finden (mehrere Methoden für Robustheit)
  let videoEl = null;
  let attempts = 0;

  while (!videoEl && attempts < 15) {
    // Methode 1: Vue ref
    if (videoElement.value) {
      videoEl = videoElement.value;
      break;
    }

    // Methode 2: DOM querySelector
    videoEl = document.querySelector('.scanner-video');
    if (videoEl) {
      break;
    }

    // Methode 3: Warten und nochmal versuchen
    await new Promise(resolve => setTimeout(resolve, 300));
    attempts++;
  }

  if (!videoEl) {
    scannerError.value = 'Video-Element nicht verfügbar - DOM nicht bereit';
    return;
  }

  // Video-Element in ref speichern falls es über querySelector gefunden wurde
  if (!videoElement.value && videoEl) {
    videoElement.value = videoEl;
  }

  // Stream direkt an Video-Element weiterleiten
  if (mediaStream.value && videoEl) {
    try {
      videoEl.srcObject = mediaStream.value;

      // Warten auf Video-Bereitschaft
      const playPromise = new Promise((resolve, reject) => {
        videoEl.onloadedmetadata = () => {
          videoEl.play().then(() => {
            resolve();
          }).catch(reject);
        };

        videoEl.onerror = (error) => {
          reject(error);
        };

        // Timeout falls Video nicht lädt
        setTimeout(() => {
          reject(new Error('Video-Timeout nach 5 Sekunden'));
        }, 5000);
      });

      await playPromise;

      // Quagga mit leichter Verzögerung starten
      setTimeout(() => {
        initQuagga(videoEl);
      }, 1000);

    } catch (error) {
      console.error('Video-Setup Fehler:', error);
      scannerError.value = 'Video konnte nicht gestartet werden: ' + error.message;
    }
  } else {
    console.error('Kein Media-Stream verfügbar');
    scannerError.value = 'Kein Kamera-Stream verfügbar';
  }
};

const initQuagga = (videoEl) => {
  // Quagga2 Configuration für verschiedene Barcode-Typen
  const config = {
    inputStream: {
      name: "Live",
      type: "LiveStream",
      target: videoEl,
      constraints: {
        width: { ideal: 640 },
        height: { ideal: 480 },
        facingMode: "environment", // Rückkamera
        aspectRatio: { ideal: 4/3 }
      }
    },
    locator: {
      patchSize: "medium",
      halfSample: true
    },
    numOfWorkers: 1, // Für iPhone reduzierte Worker
    frequency: 5, // Niedrigere Frequenz für mobile
    decoder: {
      readers: [
        "code_128_reader",
        "ean_reader",
        "ean_8_reader",
        "code_39_reader",
        "upc_reader"
      ]
    },
    locate: true
  };

  Quagga.init(config, (err) => {
    if (err) {
      scannerError.value = 'Barcode-Scanner konnte nicht gestartet werden. Versuche die manuelle Eingabe.';
      return;
    }

    scannerReady.value = true;
    Quagga.start();

    // Event-Listener für gefundene Barcodes
    Quagga.onDetected((result) => {
      const code = result.codeResult.code;

      if (code && code.length >= 7) {
        scannedValue.value = code;
        toast.success(`Barcode erkannt: ${code}`);

        // Scanner nach erfolgreicher Erkennung stoppen
        setTimeout(() => {
          Quagga.stop();
        }, 1000);
      }
    });
  });
};

const closeBarcodeScanner = () => {
  // Quagga Scanner stoppen
  try {
    Quagga.stop();
  } catch (error) {
    // Bereits gestoppt oder nicht initialisiert
  }

  // Kamera-Stream stoppen
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach(track => {
      track.stop();
    });
    mediaStream.value = null;
  }

  // Video-Element zurücksetzen
  if (videoElement.value) {
    videoElement.value.srcObject = null;
  }

  showScanner.value = false;
  scannerReady.value = false;
  scannerError.value = "";
  scannedValue.value = "";
};const useScanResult = () => {
  if (scannedValue.value) {
    // Nur numerische Werte für Auftragsnummer verwenden
    const numericValue = scannedValue.value.replace(/\D/g, '');

    if (numericValue && numericValue.length >= 7 && numericValue.length <= 10) {
      auftragsnummer.value = numericValue;
      toast.success(`Auftragsnummer ${numericValue} übernommen`);
      closeBarcodeScanner();

      // Fokus auf nächstes Feld
      nextTick(() => {
        if (auftragsnummerField.value) {
          auftragsnummerField.value.focus();
        }
      });
    } else {
      toast.warning('Gescannter Code enthält keine gültige Auftragsnummer (7-10 Ziffern)');
    }
  }
};

// ======== API-Interaktionen ========
// Instandsetzungen abrufen
const fetchInstandsetzungen = async () => {
  if (!authStore.user?.id) return;

  loading.value.fetching = true;

  try {
    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      { action: "get", typ: "Instandsetzung" },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      // Neueste Einträge zuerst sortieren
      instandsetzungen.value = response.data.auftraege.sort(
        (a, b) => new Date(b.erstelldatum) - new Date(a.erstelldatum)
      );
    } else {
      toast.error(
        "Fehler beim Laden der Instandsetzungen: " + response.data.message
      );
    }
  } catch (error) {
    console.error("Fehler beim Laden der Instandsetzungen:", error);
    toast.error("Verbindungsfehler beim Laden der Instandsetzungen.");
  } finally {
    loading.value.fetching = false;
    loading.value.initial = false;
  }
};

// Gerätetypen und Reparaturtypen abrufen
const fetchTypen = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}admin/categories.php?action=get`, {
      withCredentials: true
    });

    if (response.data.status === "success") {
      geraetetypen.value = response.data.geraetetypen || [];
      reparaturtypen.value = response.data.reparaturtypen || [];
    } else {
      toast.error("Fehler beim Laden der Kategorien: " + response.data.message);
    }
  } catch (error) {
    console.error("Fehler beim Laden der Typen:", error);
    toast.error("Verbindungsfehler beim Laden der Kategoriedaten.");
  }
};

// Typ-Namen aus ID ermitteln
const getTypName = (id, typ) => {
  const typListe =
    typ === "Gerätetyp" ? geraetetypen.value : reparaturtypen.value;
  const found = typListe.find((item) => item.id === id);
  return found ? found.name : "Unbekannt";
};

// KV-Status Hilfsfunktionen
const getKvStatusColor = (status) => {
  const statusMap = {
    'reparieren': 'success',
    'warten_et': 'warning',
    'unrepariert': 'error'
  };
  return statusMap[status] || 'grey';
};

const getKvStatusText = (status) => {
  const statusMap = {
    'reparieren': 'Reparieren',
    'warten_et': 'Warten auf Ersatzteil',
    'unrepariert': 'Unrepariert zurück'
  };
  return statusMap[status] || 'Unbekannt';
};

const typeDialogJustOpened = ref(false);

// ======== KV-Bestätigungs-Funktionen ========
// KV direkt abschließen (ohne Instandsetzung)
const completeKvDirectly = async () => {
  if (!foundKv.value) return;

  kvCompleting.value = true;

  try {
    const response = await axios.put(
      `${API_BASE_URL}kv_confirmations.php`,
      {
        action: 'complete',
        id: foundKv.value.id,
        monteur_notiz: '',
        add_to_instandsetzung: false
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success(`KV ${foundKv.value.kv_nummer} erfolgreich abgeschlossen!`);
      kvConfirmDialog.value = false;
      foundKv.value = null;
      resetForm();
    } else {
      toast.error(response.data.message || 'Fehler beim Abschließen der KV');
    }
  } catch (error) {
    console.error('Fehler beim Abschließen der KV:', error);
    toast.error('Fehler beim Abschließen der KV-Bestätigung');
  } finally {
    kvCompleting.value = false;
  }
};

// KV abschließen UND als Instandsetzung eintragen
const completeKvWithInstandsetzung = async () => {
  if (!foundKv.value) return;

  // Wenn die KV von einem anderen Monteur ist, erst übernehmen
  if (!isOwnKv.value) {
    await takeoverKv();
    if (!foundKv.value) return; // Übernahme fehlgeschlagen
  }

  // Weiter zum Type-Dialog für Geräte-/Reparaturtyp-Auswahl
  kvConfirmDialog.value = false;

  // Finde den Gerätetyp "Kleingerät" und setze ihn als Standard
  const kleingeraet = geraetetypen.value.find(
    (typ) =>
      typ.name.toLowerCase() === "kleingerät" ||
      typ.name.toLowerCase() === "kleingeraet"
  );

  if (kleingeraet) {
    geraetetyp.value = kleingeraet.id;
  }

  typeDialog.value = true;
  typeDialogJustOpened.value = true;
  setTimeout(() => {
    typeDialogJustOpened.value = false;
  }, 500);
};

// KV übernehmen (von anderem Monteur)
const takeoverKv = async () => {
  if (!foundKv.value) return;

  // Speichere die originalen Monteur-Daten VOR der Übernahme für die Notiz
  const originalMonteurVorname = foundKv.value.monteur_vorname;
  const originalMonteurNachname = foundKv.value.monteur_nachname;
  const originalMonteurNummer = foundKv.value.monteur_nummer;

  kvCompleting.value = true;

  try {
    const response = await axios.put(
      `${API_BASE_URL}kv_confirmations.php`,
      {
        action: 'takeover',
        id: foundKv.value.id
      },
      { withCredentials: true }
    );

    if (response.data.status === 'success') {
      toast.success(`KV ${foundKv.value.kv_nummer} erfolgreich übernommen!`);

      // Speichere die ORIGINALEN Daten in separaten Feldern für die Notiz
      foundKv.value.original_monteur_vorname = originalMonteurVorname;
      foundKv.value.original_monteur_nachname = originalMonteurNachname;
      foundKv.value.original_monteur_nummer = originalMonteurNummer;

      // Aktualisiere die KV-Daten auf den neuen Monteur
      foundKv.value.mitarbeiter_id = authStore.user.id;
      foundKv.value.monteur_vorname = authStore.user.vorname;
      foundKv.value.monteur_nachname = authStore.user.nachname;
      foundKv.value.monteur_nummer = authStore.user.mitarbeiternummer;

      isOwnKv.value = true;
      return true;
    } else {
      toast.error(response.data.message || 'Fehler beim Übernehmen der KV');
      foundKv.value = null;
      kvConfirmDialog.value = false;
      return false;
    }
  } catch (error) {
    console.error('Fehler beim Übernehmen der KV:', error);
    toast.error('Fehler beim Übernehmen der KV-Bestätigung');
    foundKv.value = null;
    kvConfirmDialog.value = false;
    return false;
  } finally {
    kvCompleting.value = false;
  }
};

// KV übernehmen und direkt abschließen (ohne Instandsetzung)
const takeoverAndComplete = async () => {
  if (!foundKv.value) return;

  // Erst übernehmen
  const success = await takeoverKv();
  if (!success) return;

  // Dann direkt abschließen
  await completeKvDirectly();
};

// KV-Dialog abbrechen
const cancelKvDialog = () => {
  kvConfirmDialog.value = false;
  foundKv.value = null;
  isOwnKv.value = true;

  // Normal weiter zur Instandsetzung ohne KV
  const kleingeraet = geraetetypen.value.find(
    (typ) =>
      typ.name.toLowerCase() === "kleingerät" ||
      typ.name.toLowerCase() === "kleingeraet"
  );

  if (kleingeraet) {
    geraetetyp.value = kleingeraet.id;
  }

  typeDialog.value = true;
  typeDialogJustOpened.value = true;
  setTimeout(() => {
    typeDialogJustOpened.value = false;
  }, 500);
};

// ======== Formular-Funktionen ========
// Dialog zum Auswählen von Geräte- und Reparaturtyp öffnen
const openTypeDialog = async () => {
  if (typeDialogJustOpened.value) {
    return;
  }
  if (auftragsnummer.value.length < 7 || auftragsnummer.value.length > 10) {
    toast.error("Die Auftragsnummer muss zwischen 7 und 10 Zeichen lang sein!");
    return;
  }

  // Serverseite Validierung der Auftragsnummer durchführen
  try {
    loading.value.validating = true;
    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "validate_auftragsnummer",
        auftragsnummer: auftragsnummer.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "error") {
      // Detaillierte Fehlermeldung mit Mitarbeiterinformationen
      toast.error(response.data.message);
      loading.value.validating = false;
      return;
    }

    // Prüfe, ob die Auftragsnummer eine offene KV-Bestätigung ist
    if (authStore.user?.monteur) {
      const kvResponse = await axios.get(
        `${API_BASE_URL}kv_confirmations.php`,
        {
          params: {
            action: 'check_kv_by_number',
            kv_nummer: auftragsnummer.value
          },
          withCredentials: true
        }
      );

      if (kvResponse.data.status === 'success' && kvResponse.data.data) {
        // KV-Bestätigung gefunden (eigene oder von anderem Monteur)
        foundKv.value = kvResponse.data.data;
        isOwnKv.value = kvResponse.data.isOwn;
        kvConfirmDialog.value = true;
        loading.value.validating = false;
        return;
      }
    }

    // Auftragsnummer ist verfügbar, Dialog öffnen
    // Finde den Gerätetyp "Kleingerät" und setze ihn als Standard
    const kleingeraet = geraetetypen.value.find(
      (typ) =>
        typ.name.toLowerCase() === "kleingerät" ||
        typ.name.toLowerCase() === "kleingeraet"
    );

    if (kleingeraet) {
      geraetetyp.value = kleingeraet.id;
    }

    typeDialog.value = true;
    typeDialogJustOpened.value = true;
    setTimeout(() => {
      typeDialogJustOpened.value = false;
    }, 500);
    // Dialog-Fokus setzen für Keyboard-Navigation - korrigierte Version
    nextTick(() => {
      // Versuche erst auf den "Kleingerät" Radio-Button oder einen anderen Gerätetyp zu fokussieren
      const geraeteRadioButtons = document.querySelectorAll(
        ".selection-container:first-child .v-radio"
      );
      if (geraeteRadioButtons && geraeteRadioButtons.length > 0) {
        // Suche nach dem "Kleingerät"-Radio
        const kleingeraetRadio = Array.from(geraeteRadioButtons).find(
          (radio) => {
            const label = radio.textContent.toLowerCase();
            return (
              label.includes("kleingerät") || label.includes("kleingeraet")
            );
          }
        );

        // Verwende entweder den "Kleingerät"-Radio oder den ersten Radio-Button
        const radioToFocus = kleingeraetRadio || geraeteRadioButtons[0];
        const radioInput = radioToFocus.querySelector("input");

        if (radioInput) {
          radioInput.focus();
          return;
        }
      }

      // Fallbacks für Fokussierung...
      const geraeteRadioGroup = document.querySelector(
        ".selection-container:first-child .v-radio-group"
      );
      if (geraeteRadioGroup) {
        geraeteRadioGroup.focus();
        return;
      }

      const dialogElement = document.querySelector(".type-dialog .dialog-card");
      if (dialogElement) {
        dialogElement.focus();
      }
    });
  } catch (error) {
    console.error("Fehler bei der Validierung der Auftragsnummer:", error);
    toast.error("Verbindungsfehler bei der Validierung der Auftragsnummer.");
  } finally {
    loading.value.validating = false;
  }
};

// Typ-Auswahl bestätigen und Instandsetzung speichern
const confirmTypeSelection = async () => {
  if (!geraetetyp.value || !reparaturtyp.value) {
    toast.error(
      "Bitte wähle sowohl einen Gerätetyp als auch einen Reparaturtyp aus!"
    );
    return;
  }

  // Dialog schließen und speichern
  typeDialog.value = false;

  // Wenn eine KV gefunden wurde, diese zuerst abschließen
  if (foundKv.value && authStore.user?.id) {
    try {
      kvCompleting.value = true;

      // Erstelle Notiz mit Info über vorherigen Monteur (falls übernommen)
      let notiz = 'Automatisch abgeschlossen durch Instandsetzung';

      // Prüfe ob es originale Monteur-Daten gibt (nach Übernahme gespeichert)
      if (foundKv.value.original_monteur_vorname && foundKv.value.original_monteur_nachname) {
        notiz += ` (übernommen von ${foundKv.value.original_monteur_vorname} ${foundKv.value.original_monteur_nachname}`;
        if (foundKv.value.original_monteur_nummer) {
          notiz += ` - Nr. ${foundKv.value.original_monteur_nummer}`;
        }
        notiz += ')';
      }

      const kvResponse = await axios.put(
        `${API_BASE_URL}kv_confirmations.php`,
        {
          action: 'complete',
          id: foundKv.value.id,
          monteur_notiz: notiz,
          add_to_instandsetzung: true,
          instandsetzung_mitarbeiter_id: authStore.user.id,
          geraetetyp_id: geraetetyp.value,
          reparaturtyp_id: reparaturtyp.value
        },
        { withCredentials: true }
      );

      if (kvResponse.data.status === 'success') {
        toast.success(`KV ${foundKv.value.kv_nummer} automatisch abgeschlossen und in Instandsetzung eingetragen!`);
        foundKv.value = null;
        kvCompleting.value = false;

        // Formular zurücksetzen und neu laden
        resetForm();
        await fetchInstandsetzungen();
        return;
      } else {
        toast.error(kvResponse.data.message || 'Fehler beim Abschließen der KV');
        kvCompleting.value = false;
      }
    } catch (error) {
      console.error('Fehler beim Abschließen der KV:', error);
      toast.error('Fehler beim Abschließen der KV-Bestätigung');
      kvCompleting.value = false;
    }
  }

  // Normal weiter ohne KV
  await submitInstandsetzung();

  // Nach dem Speichern verhindern wir weitere Enter-Aktionen kurzzeitig
  typeDialogJustOpened.value = true;
  setTimeout(() => {
    typeDialogJustOpened.value = false;
  }, 500);
};

// Instandsetzung in der Datenbank speichern
const submitInstandsetzung = async () => {
  try {
    if (!authStore.user?.id) {
      toast.error("Bitte melde dich an, um Instandsetzungen zu erfassen.");
      return;
    }

    if (!formValid.value) {
      toast.error("Bitte alle Felder korrekt ausfüllen!");
      return;
    }

    formSubmitting.value = true;

    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "add",
        typ: "Instandsetzung",
        geraetetyp_id: geraetetyp.value,
        reparaturtyp_id: reparaturtyp.value,
        auftragsnummer: auftragsnummer.value,
        menge: menge.value,
        kommentar: kommentar.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      await fetchInstandsetzungen();
      toast.success("Instandsetzung erfolgreich gespeichert!");
      resetForm();
    } else {
      toast.error(response.data.message || "Fehler beim Speichern!");
    }
  } catch (error) {
    console.error("Fehler beim Speichern der Instandsetzung:", error);
    toast.error("Verbindungsfehler beim Speichern der Instandsetzung.");
  } finally {
    formSubmitting.value = false;
  }
};

// Formular zurücksetzen
const resetForm = () => {
  auftragsnummer.value = "";
  menge.value = 1;
  kommentar.value = "";
  geraetetyp.value = null;
  reparaturtyp.value = null;

  // Nach dem Zurücksetzen Fokus wieder auf das Auftragsnummernfeld setzen
  nextTick(() => {
    if (auftragsnummerField.value) {
      auftragsnummerField.value.focus();
    }
  });
};

// ======== Bearbeiten-Funktionen ========
// Bearbeiten-Dialog öffnen
const editInstandsetzung = (item) => {
  editItem.value = { ...item };

  // Datum für das HTML datetime-local Input formatieren (YYYY-MM-DDTHH:MM)
  try {
    const date = new Date(item.erstelldatum);
    if (!isNaN(date.getTime())) {
      // Lokale Zeit verwenden statt toISOString (verhindert Zeitzonenverschiebung)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");

      // Format für datetime-local Input
      editItem.value.erstelldatum = `${year}-${month}-${day}T${hours}:${minutes}`;
    }
  } catch (e) {
    console.error("Fehler bei der Datumskonvertierung:", e);
  }

  editDialog.value = true;
};

// Neue Funktion für Enter-Taste im Dialog
const handleDialogEnter = (e) => {
  // Stoppe die Event-Propagation
  e.stopPropagation();
  e.preventDefault();

  // Prüfe, ob beide Typen ausgewählt sind
  if (geraetetyp.value && reparaturtyp.value) {
    confirmTypeSelection();
  }
};

// Instandsetzung aktualisieren
const updateInstandsetzung = async () => {
  try {
    if (editItem.value.auftragsnummer.length < 7 || editItem.value.auftragsnummer.length > 10) {
      toast.error("Die Auftragsnummer muss zwischen 7 und 10 Zeichen lang sein!");
      return;
    }

    loading.value.updating = true;

    // Validierung der Auftragsnummer beim Update
    const validateResponse = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "validate_auftragsnummer",
        auftragsnummer: editItem.value.auftragsnummer,
        exclude_id: editItem.value.id, // Aktuellen Eintrag ausschließen
      },
      { withCredentials: true }
    );

    if (validateResponse.data.status === "error") {
      toast.error(validateResponse.data.message);
      loading.value.updating = false;
      return;
    }

    // Zeitzonenkorrektur für das Datum
    let formattedDate;
    if (editItem.value.erstelldatum) {
      // Date-Objekt erstellen
      const date = new Date(editItem.value.erstelldatum);

      // MySQL-kompatibles Format OHNE Zeitzoneninformation
      // Format: 'YYYY-MM-DD HH:MM:SS'
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");

      formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } else {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");

      formattedDate = `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    // Rest des Update-Prozesses bleibt unverändert
    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "update",
        id: editItem.value.id,
        typ: "Instandsetzung",
        geraetetyp_id: editItem.value.geraetetyp_id,
        reparaturtyp_id: editItem.value.reparaturtyp_id,
        auftragsnummer: editItem.value.auftragsnummer,
        menge: editItem.value.menge,
        kommentar: editItem.value.kommentar,
        erstelldatum: formattedDate,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      await fetchInstandsetzungen();
      toast.success("Instandsetzung erfolgreich aktualisiert!");
      editDialog.value = false;
    } else {
      toast.error(response.data.message || "Fehler beim Aktualisieren!");
    }
  } catch (error) {
    console.error("Fehler beim Aktualisieren der Instandsetzung:", error);
    toast.error("Verbindungsfehler beim Aktualisieren.");
  } finally {
    loading.value.updating = false;
  }
};

// ======== Löschen-Funktionen ========
// Löschen-Dialog öffnen
const confirmDelete = (id) => {
  deleteId.value = id;
  deleteDialog.value = true;
};

// Instandsetzung löschen
const deleteInstandsetzung = async () => {
  try {
    loading.value.deleting = true;

    const response = await axios.post(
      `${API_BASE_URL}auftraege.php`,
      {
        action: "delete",
        id: deleteId.value,
      },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      await fetchInstandsetzungen();
      toast.success("Instandsetzung erfolgreich gelöscht!");
      deleteDialog.value = false;
    } else {
      toast.error(response.data.message || "Fehler beim Löschen!");
    }
  } catch (error) {
    console.error("Fehler beim Löschen der Instandsetzung:", error);
    toast.error("Verbindungsfehler beim Löschen.");
  } finally {
    loading.value.deleting = false;
  }
};

// Beobachter für Authentifizierungsänderungen
watch(
  () => authStore.user,
  (newUser, oldUser) => {
    if (newUser && !oldUser) {
      fetchInstandsetzungen();
    } else if (!newUser && oldUser) {
      instandsetzungen.value = [];
    }
  }
);

// ======== Lebenszyklus-Hooks ========

// Such-Dialog-Filter beim Schließen zurücksetzen
watch(instSearchDialog, (isOpen) => {
  if (!isOpen) {
    instSearchQuery.value = '';
    searchDialogMonth.value = null;
    searchDialogGeraetetyp.value = null;
    searchDialogReparaturtyp.value = null;
  }
});

// Daten beim Laden der Komponente abrufen
onMounted(() => {
  fetchTypen();
  if (authStore.user) {
    fetchInstandsetzungen();
  }

  // Automatisch den Fokus auf das Auftragsnummernfeld setzen
  nextTick(() => {
    if (auftragsnummerField.value) {
      // Fokussieren und das gesamte Feld selektieren
      auftragsnummerField.value.focus();
    }
  });

  // Tastaturkürzel für Escape-Taste hinzufügen
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      if (typeDialog.value) typeDialog.value = false;
      else if (editDialog.value) editDialog.value = false;
      else if (deleteDialog.value) deleteDialog.value = false;
      else if (instSearchDialog.value) instSearchDialog.value = false;
    }
  });
});

// IRIS Matrix Zustände
const errorTypes = [
  '1', '4', '3', '7', '9', 'A', 'C', 'D', 'D1', 'G1', 'G3', 'H1', 'K', 'M1', 'N', 'Q', 'T', '92'
];

const matrixData = [
  { section: 'Bedieneinheit', code: 'G07', types: ['1', '4', 'N'] },
  { section: 'Verdrahtung', code: 'G08', types: ['7', 'D'] },
  { section: 'Verbindung / Kontakt', code: 'G09', types: ['7', 'T'] },
  { section: 'Elektronik PCB', code: 'G12', types: ['1', 'N', 'Q'] },
  { section: 'Mikroschalter', code: 'G16', types: ['M1', 'N'] },
  { section: 'Brühgruppe', code: 'G18', types: ['A', 'D1', 'K', 'M1', 'N', '92'] },
  { section: 'Dichtung', code: 'G30', types: ['A', '92'] },
  { section: 'Wassersystem/Schläuche', code: 'G33', types: ['7', 'D', 'D1', 'M1', '92'] },
  { section: 'Filter', code: 'G34', types: ['9', 'K', 'M1'] },
  { section: 'Kondensor', code: 'G35', types: ['K', 'M1'] },
  { section: 'Flowmeter', code: 'G41', types: ['A', 'K', 'M1', 'N', '92'] },
  { section: 'Chassis/Bodenplatte/Gehäuse', code: 'G47', types: ['7', 'D', 'G1', 'G3', 'H1'] },
  { section: 'Potentiometer', code: 'G05', types: ['7', 'N'] },
  { section: 'Abtropfschale/Reste-Behälter', code: 'G55', types: ['9', 'A', 'D', 'G3', '92'] },
  { section: 'Falsche Benutzung', code: 'G61', types: ['4', '3'] },
  { section: 'Tank/Wasserversorgung', code: 'G70', types: ['D', 'K', 'N', '92'] },
  { section: 'Pumpe', code: 'G72', types: ['A', 'N'] },
  { section: 'Dampfdüse', code: 'G78', types: ['3', '7', '9', 'A', 'M1', 'N', '92'] },
  { section: 'Verteiler / Distributor', code: 'G80', types: ['A', 'K', 'M1', 'N'] },
  { section: 'Mahlwerk', code: 'G82', types: ['7', '9', 'A', 'C', 'M1', 'N'] },
  { section: 'Kaffeeauslauf', code: 'B01', types: ['4', 'A', 'D1', 'K', '92'] },
];

const errorTypeLegend = {
  '1': 'Software-Problem',
  '4': 'Kein Fehler feststellbar',
  '3': 'Kein Problem (Gerät innerhalb der Spez.)',
  '7': 'Falsch verdrahtet/zusammengebaut',
  '9': 'Fehlbedienung',
  'A': 'Abgenutzt/verschlissen oder mechanisch defekt',
  'C': 'Mechanische Fehleinstellung',
  'D': 'Abgetrennt/gebrochen',
  'D1': 'Riss/gerissen',
  'G1': 'Kratzer oder äußere Beschädigung',
  'G3': 'Scharfkantig',
  'H1': 'Rissig/abgeblättert',
  'K': 'Undicht',
  'M1': 'Verstopft/blockiert',
  'N': 'Elektrisches Bauteil defekt',
  'Q': 'Elektrischer Kurzschluss',
  'T': 'Schlechte Kontaktverbindung',
  '92': 'Schlechte Wartung',
};

const filteredIrisRows = computed(() => {
  if (!irisSearchCode.value) return matrixData;
  return matrixData.filter((row) =>
    row.code.toLowerCase().includes(irisSearchCode.value.toLowerCase())
  );
});
</script>

<style scoped>
/* ======== Container & Basis-Layout ======== */
.instandsetzung-container {
  max-width: 1200px;
  margin: 0 auto;
  padding-top: 24px;
  padding-bottom: 32px;
}

/* ======== Karten & Container ======== */
.filter-card {
  background-color: rgba(30, 30, 35, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.filter-card:hover {
  border-color: rgba(76, 175, 80, 0.3);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.filter-card .v-card-title {
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.1) 0%,
    rgba(76, 175, 80, 0.05) 100%
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  padding: 12px 16px;
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.gap-2 {
  gap: 8px;
}

.status-overview-card {
  background-color: rgba(30, 30, 35, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 24px;
  transition: all 0.3s ease;
}

.card-divider {
  opacity: 0.1;
}

.maximized-card {
  height: calc(100vh - 180px);
  display: flex;
  flex-direction: column;
}

.dialog-card {
  background-color: rgba(30, 30, 35, 0.9);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
}

.dialog-title {
  padding: 16px;
  font-size: 1.1rem;
  font-weight: 500;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.dialog-divider {
  opacity: 0.1;
}

.dialog-content {
  padding: 20px;
}

.dialog-actions {
  padding: 12px 16px;
}

.warning-header {
  background: rgba(var(--v-theme-error), 0.1);
}

/* ======== Eingabefelder & Formulare ======== */
.form-field {
  margin-bottom: 12px;
}

.form-field :deep(.v-field__input) {
  color: rgba(255, 255, 255, 0.85);
}

.form-field :deep(.v-field__outline) {
  color: rgba(255, 255, 255, 0.12);
}

.form-field :deep(.v-field:hover .v-field__outline) {
  color: rgba(255, 255, 255, 0.18);
}

.submit-btn {
  height: 50px;
  border-radius: 12px !important;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: none;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.submit-btn[disabled] {
  opacity: 0.65 !important;
  box-shadow: none !important;
  background-color: rgba(60, 60, 60, 0.8) !important;
  color: rgba(255, 255, 255, 0.5) !important;
}

.submit-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: 0.6s;
}

.submit-btn:hover:not([disabled]) {
  transform: translateY(-2px);
  box-shadow: 0 6px 15px rgba(76, 175, 80, 0.4) !important;
}

.submit-btn:hover:not([disabled])::before {
  left: 100%;
}

.search-field :deep(.v-field__prepend-inner) {
  opacity: 0.8;
}

/* ======== Typ-Auswahl im Dialog ======== */
.selection-container {
  margin-bottom: 16px;
}

.selection-title {
  font-size: 1rem;
  margin-bottom: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.selection-divider {
  opacity: 0.1;
}

.radio-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.type-radio :deep(.v-selection-control__wrapper) {
  opacity: 0.9;
}

.type-radio :deep(.v-label) {
  opacity: 0.85;
}

/* ======== Tabellen-Design ======== */
.data-table {
  background: transparent !important;
}

.data-table :deep(table) {
  background: transparent !important;
  border-spacing: 0;
}

.data-table :deep(tr:hover td) {
  background: rgba(255, 255, 255, 0.05) !important;
}

.data-table :deep(th) {
  background: rgba(20, 20, 25, 0.5) !important;
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
}

.data-table :deep(td) {
  padding: 12px 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05) !important;
  color: rgba(255, 255, 255, 0.85);
}

/* Paginierung anpassen */
.data-table :deep(.v-data-table-footer) {
  background: transparent !important;
}

.data-table :deep(.v-data-table-footer__items-per-page) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-select .v-field) {
  background: rgba(30, 30, 40, 0.5) !important;
  color: rgba(255, 255, 255, 0.8) !important;
}

.data-table :deep(.v-data-table-footer .v-btn) {
  color: rgba(255, 255, 255, 0.7) !important;
}

.data-table :deep(.v-data-table-footer .v-btn--disabled) {
  color: rgba(255, 255, 255, 0.3) !important;
}

.date-cell {
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.date-cell:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.auftrags-cell {
  font-family: "Roboto Mono", monospace;
  letter-spacing: 0.5px;
}

.typ-chip {
  font-size: 0.75rem !important;
  font-weight: 500 !important;
}

.geraete-chip {
  background: rgba(76, 175, 80, 0.15) !important;
  color: rgba(76, 175, 80, 0.9) !important;
}

.reparatur-chip {
  background: rgba(76, 175, 80, 0.15) !important;
  color: rgba(76, 175, 80, 0.9) !important;
}

.kommentar-cell {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}

.menge-cell {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

/* ======== Aktionsbuttons in Tabellen ======== */
.action-buttons-cell {
  width: 110px;
}

.action-buttons {
  display: flex;
  justify-content: center;
}

.action-btn {
  opacity: 0.8;
  transition: all 0.2s ease;
}

.action-btn:hover {
  opacity: 1;
  transform: translateY(-2px);
}

.edit-btn:hover {
  box-shadow: 0 3px 8px rgba(76, 175, 80, 0.3);
}

.delete-btn:hover {
  box-shadow: 0 3px 8px rgba(var(--v-theme-error), 0.3);
}

/* ======== Leerstand & Platzhaltermeldungen ======== */
.no-data-message,
.no-results,
.search-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
}

.search-placeholder {
  padding: 60px 20px;
}

.search-results {
  max-height: 400px;
  overflow-y: auto;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 960px) {
  .radio-options {
    flex-direction: column;
    gap: 0;
  }

  .action-buttons-cell {
    width: 90px;
  }
}

@media (max-width: 600px) {
  .dialog-content {
    padding: 16px;
  }

  .dialog-title {
    font-size: 1rem;
    padding: 12px 16px;
  }

  .selection-title {
    font-size: 0.9rem;
  }

  .data-table :deep(th),
  .data-table :deep(td) {
    padding: 8px 12px !important;
  }

  .typ-chip {
    font-size: 0.7rem !important;
  }

  .search-placeholder {
    padding: 40px 16px;
  }
}

/* Deutlichere Stile für deaktivierte Buttons */
.disabled-btn {
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  background-color: rgba(60, 60, 60, 0.8) !important;
  color: rgba(255, 255, 255, 0.5) !important;
  opacity: 0.9 !important;
}

.disabled-btn:hover {
  transform: none !important;
  box-shadow: none !important;
}

.disabled-btn::before {
  display: none !important;
}

/* Durchgestrichenes Icon für deaktivierten Zustand */
.keyboard-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-right: 8px;
}

.strike-through {
  position: absolute;
  display: block;
  width: 130%;
  height: 2px;
  background-color: rgba(255, 80, 80, 0.7);
  transform: rotate(-45deg);
  left: -15%;
  top: 50%;
}

.submit-btn[disabled] {
  opacity: 1 !important;
  box-shadow: none !important;
  cursor: not-allowed;
}

/* Drei-Punkte-Menü für Aktionen */
.action-buttons-cell {
  width: 48px !important;
  text-align: center;
}

.action-menu-wrapper {
  display: flex;
  justify-content: center;
}

.action-menu-btn {
  opacity: 0.7;
  transition: all 0.2s ease;
}

.action-menu-btn:hover {
  opacity: 1;
  transform: scale(1.1);
}

.action-menu-list {
  background-color: rgba(35, 35, 40, 0.95) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  overflow: hidden;
  min-width: 180px;
}

.action-menu-item {
  transition: background-color 0.2s ease;
}

.edit-action:hover {
  background-color: rgba(76, 175, 80, 0.1) !important;
}

.edit-action:hover :deep(.v-list-item-title),
.edit-action:hover :deep(.v-icon) {
  color: rgba(76, 175, 80, 0.9) !important;
}

.delete-action:hover {
  background-color: rgba(244, 67, 54, 0.1) !important;
}

.delete-action:hover :deep(.v-list-item-title),
.delete-action:hover :deep(.v-icon) {
  color: rgba(244, 67, 54, 0.9) !important;
}

.menu-divider {
  opacity: 0.1;
}

/* IRIS Matrix Styles */
.matrix-table-wrapper {
  overflow-x: auto;
  background: rgba(30, 30, 40, 0.7);
  border-radius: 12px;
  margin-top: 16px;
  max-height: 500px;
  overflow-y: auto;
}

.iris-matrix-table {
  width: 100%;
  min-width: 800px;
  border-collapse: separate;
  border-spacing: 0;
  color: #fff;
  font-size: 0.85rem;
}

.iris-matrix-table th,
.iris-matrix-table td {
  padding: 4px 6px;
  text-align: center;
  border-bottom: 1px solid #2e7d32;
  white-space: nowrap;
}

.iris-matrix-table th.sticky-header {
  position: sticky;
  top: 0;
  background: rgba(30, 30, 40, 0.95);
  z-index: 2;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: rgba(30, 30, 40, 0.95);
  z-index: 3;
  min-width: 100px;
}

.sticky-col.left2 {
  left: 100px;
  z-index: 3;
  min-width: 60px;
}

.iris-matrix-table th.sticky-col.left2,
.iris-matrix-table td.sticky-col.left2 {
  left: 100px;
}

.iris-matrix-table th.sticky-col.left,
.iris-matrix-table td.sticky-col.left {
  left: 0;
}

.zebra {
  background: rgba(76, 175, 80, 0.07);
}

.matrix-x {
  text-align: center;
  padding: 2px !important;
}

.matrix-x-pill {
  display: inline-block;
  background: linear-gradient(90deg, #43a047 60%, #66bb6a 100%);
  color: #fff;
  font-weight: 600;
  border-radius: 999px;
  padding: 0 8px;
  font-size: 0.8rem;
  box-shadow: 0 2px 4px rgba(67, 160, 71, 0.1);
  letter-spacing: 0.05em;
  transition: background 0.2s;
  cursor: pointer;
  margin: 0 auto;
  min-width: 20px;
  height: 20px;
  line-height: 20px;
}

.matrix-x-pill:hover {
  background: linear-gradient(90deg, #388e3c 60%, #43a047 100%);
}

.legend-container {
  background: rgba(30, 30, 40, 0.85);
  border-radius: 12px;
  margin-top: 16px;
  padding: 12px 16px;
  box-shadow: 0 2px 8px rgba(67, 160, 71, 0.08);
  color: #fff;
  max-width: 100%;
}

.legend-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: #a5d6a7;
}

.legend-grid {
  display: grid;
  grid-template-columns: 1fr 4fr;
  gap: 4px 8px;
  font-size: 0.8rem;
}

.legend-row {
  display: contents;
}

.legend-key {
  font-weight: bold;
  color: #43a047;
  text-align: right;
}

.legend-desc {
  color: #fff;
  opacity: 0.92;
}

@media (max-width: 900px) {
  .iris-matrix-table {
    font-size: 0.8rem;
    min-width: 700px;
  }
  .sticky-col.left2 {
    left: 90px;
  }
  .matrix-x-pill {
    padding: 0 6px;
    font-size: 0.75rem;
    min-width: 18px;
    height: 18px;
    line-height: 18px;
  }
}

/* Anpassung des Dialogs für bessere Kompaktheit */
.iris-matrix-dialog .dialog-content {
  padding: 12px !important;
}

.iris-matrix-dialog .dialog-title {
  padding: 12px !important;
  font-size: 1rem !important;
}

.iris-matrix-dialog .form-field {
  margin-bottom: 8px !important;
}

.iris-matrix-dialog .v-text-field {
  font-size: 0.9rem !important;
}

/* Spare Parts Dialog Styles */
.spare-parts-dialog .dialog-content {
  padding: 0 !important;
}

.spare-parts-dialog :deep(.v-card) {
  box-shadow: none !important;
}

.spare-parts-dialog :deep(.v-container) {
  padding: 0 !important;
}

/* Barcode Scanner Styles */
.scanner-card {
  background: linear-gradient(135deg, rgba(30, 30, 40, 0.95) 0%, rgba(40, 40, 50, 0.95) 100%) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(67, 160, 71, 0.2);
}

.scanner-container {
  min-height: 200px;
  position: relative;
}

.camera-container {
  position: relative;
  width: 100%;
  height: 300px;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
}

.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.scanner-frame {
  width: 200px;
  height: 200px;
  border: 3px solid #43a047;
  border-radius: 8px;
  background: transparent;
  position: relative;
  box-shadow:
    0 0 0 9999px rgba(0, 0, 0, 0.4),
    0 0 20px rgba(67, 160, 71, 0.6);
}

.scanner-frame::before {
  content: '';
  position: absolute;
  top: -3px;
  left: -3px;
  right: -3px;
  bottom: -3px;
  border: 3px solid transparent;
  border-top-color: #43a047;
  border-right-color: #43a047;
  border-radius: 8px;
  animation: scannerPulse 2s linear infinite;
}

@keyframes scannerPulse {
  0% {
    border-top-color: #43a047;
    border-right-color: transparent;
    border-bottom-color: transparent;
    border-left-color: transparent;
  }
  25% {
    border-top-color: #43a047;
    border-right-color: #43a047;
    border-bottom-color: transparent;
    border-left-color: transparent;
  }
  50% {
    border-top-color: #43a047;
    border-right-color: #43a047;
    border-bottom-color: #43a047;
    border-left-color: transparent;
  }
  75% {
    border-top-color: #43a047;
    border-right-color: #43a047;
    border-bottom-color: #43a047;
    border-left-color: #43a047;
  }
  100% {
    border-top-color: #43a047;
    border-right-color: #43a047;
    border-bottom-color: #43a047;
    border-left-color: #43a047;
  }
}

.error-container {
  padding: 20px;
}

.scanner-btn {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.scanner-btn:hover {
  opacity: 1;
}

/* KV-Bestätigungs-Dialog Styles */
.kv-confirm-dialog .dialog-card {
  background: rgba(30, 30, 35, 0.95);
  backdrop-filter: blur(10px);
}

.kv-details-card {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.detail-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 6px;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.options-list {
  background: transparent !important;
}

.option-item {
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-bottom: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.option-item:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(76, 175, 80, 0.5);
  transform: translateX(4px);
}

.option-item-primary {
  border-color: rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.05);
}

.option-item-primary:hover {
  background: rgba(76, 175, 80, 0.15);
  border-color: rgba(76, 175, 80, 0.7);
}
</style>
